// packages import
import { Box, Chip, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material'
import moment from 'moment-timezone'
import { FC, useContext } from 'react'
import { Link } from 'react-router-dom'
import { CasesMenu } from '../../menus/Cases'
// others
import { EventType, ExternalUserCaseRole, UserRole } from '../../../../generated'
import { DCPP, NOT_APPLICABLE, STANDARD_DATE_FORMAT, TIME_WITH_SECONDS_FORMAT } from '../../../constants'
import { AuthContext, CaseContext } from '../../../context'
import { DisplayCaseItemProps } from '../../../interfaceTypes'
import { capitalizeCase, hasAdminAccess, titleize } from '../../../utils'

export const DisplayCaseItem: FC<DisplayCaseItemProps> = ({ IsMyCases }): JSX.Element => {
  const { allCases } = useContext(CaseContext)
  const { currentUser, isDcpp } = useContext(AuthContext)
  const filterAllCases = allCases?.filter(litigation => {
    const { caseType } = litigation || {}
    const { name } = caseType || {}

    return name !== DCPP
  })
  const cases = hasAdminAccess(currentUser) || IsMyCases || isDcpp ? allCases : filterAllCases

  return (
    <>
      {cases?.map(caseObj => {
        const {
          caseEvents,
          caseNumber,
          consultMatterNumber,
          isConsultMatter,
          caseToExternalUserToExternalUserRole,
          caseToUsers,
          caseType,
          id,
          title,
          court,
          updatedAt,
          advCaseNumber
        } = caseObj || {}

        const { name: caseTypeName, lawType } = caseType || {}
        const { name: lawTypeName } = lawType || {}

        const attorney = caseToUsers?.filter(
          attorneyUser => attorneyUser?.user?.roles && attorneyUser?.user?.roles[0]?.role === UserRole.Attorney
        )
        const paralegal = caseToUsers?.filter(
          paralegalUser =>
            paralegalUser?.user?.roles &&
            paralegalUser?.user?.roles[0]?.role === UserRole.Paralegal &&
            paralegalUser?.userType !== 'CREATOR'
        )

        const clientInfo = caseToExternalUserToExternalUserRole?.find(
          item => item?.caseRole === ExternalUserCaseRole.PrimaryClient
        )?.externalUserToExternalUserRole.externalUser

        const { displayName, clientNumber } = clientInfo || {}
        const userFullName = `${!!clientNumber ? `(${clientNumber})` : ''} ${displayName || '-'}`

        const { name: courtName } = court || {}
        const courtDateType = caseEvents?.filter(date => date?.eventType === EventType.CourtDate)
        const hearingAtDates = courtDateType?.map(element => element?.hearingAt)
        const upcomingDates = hearingAtDates
          ?.map(dateStr => parseInt(dateStr as string))
          .filter(timestamp =>
            moment
              .tz(moment.unix(parseInt(timestamp.toString()) / 1000), 'UTC')
              .isAfter(moment.tz(moment.unix(parseInt(new Date().getTime().toString()) / 1000), 'UTC'))
          )
        const upcomingDate = upcomingDates && upcomingDates.length > 0 ? new Date(Math.min(...upcomingDates)) : ''
        const displayUpcomingDate = moment
          .tz(moment.unix(parseInt(new Date(upcomingDate).getTime().toString()) / 1000), 'UTC')
          .format('MM/DD/YYYY')

        return (
          <TableRow key={id} hover>
            <DisplayTableCell
              title={(isConsultMatter ? `${consultMatterNumber}` : `${caseNumber}`) || NOT_APPLICABLE}
              content={
                '#' +
                ((isConsultMatter ? `${consultMatterNumber}` : `${caseNumber}`) ||
                  consultMatterNumber ||
                  NOT_APPLICABLE)
              }
              to={`/matters/matter-detail/${id}`}
              caseId={id}
            />

            <TableCell>
              <Box>
                <Tooltip
                  title={isConsultMatter ? consultMatterNumber : advCaseNumber || NOT_APPLICABLE}
                  arrow
                  placement="bottom-start"
                >
                  <Typography variant="body1" noWrap color="primary">
                    {isConsultMatter ? consultMatterNumber : advCaseNumber || consultMatterNumber || NOT_APPLICABLE}
                  </Typography>
                </Tooltip>
              </Box>
            </TableCell>

            <TableCell>
              <Tooltip title={title || '--'} arrow>
                <Typography variant="body2" fontWeight={400}>
                  {title || '--'}
                </Typography>
              </Tooltip>
            </TableCell>

            <TableCell>
              <Tooltip title={caseTypeName || '--'} arrow>
                <Typography variant="body1" textTransform="capitalize" noWrap maxWidth="120px">
                  {lawTypeName || '--'}
                </Typography>
              </Tooltip>
            </TableCell>

            <TableCell>
              <Tooltip title={userFullName || '--'} arrow>
                <Link to={`/contacts/client/${clientNumber}`}>
                  <Typography variant="body1" color={'primary'}>
                    {userFullName}
                  </Typography>
                </Link>
              </Tooltip>
            </TableCell>

            <TableCell sx={{ maxWidth: '150px' }}>
              {paralegal?.length === 0 ? (
                <Typography>{'--'}</Typography>
              ) : (
                <>
                  <Stack direction="row" flexWrap="wrap" gap="10px">
                    {paralegal?.slice(0, 2).map(assignedUser => {
                      const { user } = assignedUser || {}
                      const { firstName, lastName } = user || {}
                      const userFullName = `${firstName} ${lastName}`

                      return (
                        <Chip
                          key={`${id}-${firstName}`}
                          label={capitalizeCase(userFullName || '')}
                          variant="outlined"
                        />
                      )
                    })}
                  </Stack>

                  {paralegal?.length! > 2 && (
                    <Tooltip
                      title={paralegal?.slice(2)?.map(assignedUser => {
                        const { user, id } = assignedUser || {}
                        const { firstName, lastName } = user || {}
                        const userFullName = `${firstName} ${lastName}`

                        return (
                          <Typography color="white" key={id}>
                            {capitalizeCase(userFullName || '')}
                          </Typography>
                        )
                      })}
                      sx={{ gap: '10px' }}
                      placement="bottom-start"
                    >
                      <Box pt={1}>
                        <Chip
                          key={id}
                          color="primary"
                          label={`+${paralegal?.length && paralegal?.length - 2}`}
                          variant="outlined"
                        />
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
            </TableCell>

            <TableCell>
              {attorney?.length === 0 ? (
                <Typography>--</Typography>
              ) : (
                <>
                  <Stack direction="row" flexWrap="wrap" gap="10px">
                    {attorney?.slice(0, 2).map(assignedUser => {
                      const { user, id } = assignedUser || {}
                      const { firstName, lastName } = user || {}
                      const userFullName = `${firstName} ${lastName}`

                      return (
                        <Chip key={`${id}-${firstName}`} label={capitalizeCase(userFullName || '')} variant="filled" />
                      )
                    })}
                  </Stack>

                  {attorney?.length! > 2 && (
                    <Tooltip
                      title={attorney?.slice(2)?.map(assignedUser => {
                        const { user, id } = assignedUser || {}
                        const { firstName, lastName } = user || {}
                        const userFullName = `${firstName} ${lastName}`

                        return (
                          <Typography color="white" key={id}>
                            {capitalizeCase(userFullName || '')}
                          </Typography>
                        )
                      })}
                      sx={{ gap: '10px' }}
                      placement="bottom-start"
                    >
                      <Box pt={1}>
                        <Chip
                          key={id}
                          label={`+${attorney?.length && attorney?.length - 2}`}
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
            </TableCell>

            <TableCell>
              <Tooltip title={titleize(courtName || '--')} arrow>
                <Typography variant="body1" noWrap maxWidth="120px" textTransform="capitalize">
                  {titleize(courtName || '--')}
                </Typography>
              </Tooltip>

              {upcomingDates && upcomingDates.length > 0 && (
                <Tooltip title={upcomingDate && displayUpcomingDate} arrow>
                  <Typography noWrap color="wardGray" fontSize="14px">
                    {displayUpcomingDate}
                  </Typography>
                </Tooltip>
              )}
            </TableCell>

            <TableCell>
              <Typography variant="body2" noWrap maxWidth="150px" fontWeight={400}>
                {updatedAt ? moment(parseInt(updatedAt)).format(STANDARD_DATE_FORMAT) : '--'}
              </Typography>
              <Typography noWrap color="wardGray" fontSize="14px">
                {updatedAt ? moment(parseInt(updatedAt)).format(TIME_WITH_SECONDS_FORMAT) : '--'}
              </Typography>
            </TableCell>

            <TableCell align="center">
              <CasesMenu currentCase={caseObj} />
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

const DisplayTableCell = ({ content, title, to, caseId }: { content?: string; title?: string; to?: string; caseId?: string }) => {
  return (
    <TableCell>
      <Box display="flex" flexDirection="column" gap={0.5}>
        {to ? (
          <Link to={to}>
            <Tooltip title={title || '--'} arrow>
              <Typography variant="body2" fontWeight={400} color={'primary'}>
                {content || '--'}
              </Typography>
            </Tooltip>
          </Link>
        ) : (
          <Tooltip title={title || '--'} arrow>
            <Typography variant="body2" fontWeight={400}>
              {content || '--'}
            </Typography>
          </Tooltip>
        )}
        {caseId && (
          <Link to={`/matters/matter-detail/${caseId}?active-tab=2`}>
            <Tooltip title="View Financials" arrow>
              <Typography variant="body2" fontWeight={400} color={'primary'} sx={{ fontSize: '0.875rem' }}>
                Financials
              </Typography>
            </Tooltip>
          </Link>
        )}
      </Box>
    </TableCell>
  )
}
